import { Title, LoadingOverlay } from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import InformationTab from "../generic/tabs/InformationTab";
import { ObjectData, STARA_OBJECT_FIELD_DEFAULTS } from "../generic/types/ObjectFormTypes";
import useTransientStore from "../../data/transcientStore";
import {
  createPolyline,
  createPolypoint,
  createPolygon,
  getContractsByAddressSystemAndUser,
} from "../../services/MapComponentsService";
import { searchGeometries } from "../../services/GeometrySearchService";
import { CreateGeometryRequest } from "../../types/Geometry";
import "../../styles/notifications.css";
import { usePermissions } from "../../hooks/usePermissions";
import { useTranslation } from "../../hooks/useTranslation";

interface AddActionMenuProps {
  onDrawingStateChange?: (isDrawing: boolean) => void;
}

export default function AddActionMenu({
  onDrawingStateChange,
}: AddActionMenuProps) {
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const triggerClearDrawing = useTransientStore(
    (state) => state.triggerClearDrawing,
  );

  // Get addressSystemId and userId from store
  const selectedAddressSystem = useTransientStore(
    (state) => state.selectedAddressSystem,
  );
  const currentUserId = useTransientStore((state) => state.currentUserId);
  const addrIdFromStore = selectedAddressSystem?.id || 0;

  const { getTranslation } = useTranslation({
    pageId: 332,
    addressSystemId: addrIdFromStore,
    autoLoad: true,
  });

  const { checkAccess } = usePermissions();
  const canAddObject = checkAccess("CITY_OBJECT_ADD");

  const form = useForm<ObjectData>({
    initialValues: {
      id: 0,
      wkt: "",
      drawingType: "",
      job: "",
      name: "",
      streetName: "",
      orderName: "",
      networkName: "",
      status: "",
      type: "",
      typeDesc: "",
      plannedYear: null,
      measures: "",
      lanes: "",
      levelOfReadiness: "",
      subscriber: null,
      foreman: null,
      contractorForeman: null,
      projectManager: null,
      qualityControl: "",
      qualityControlComments: "",
      pvm: null,
      agreedPvm: null,
      completedPvm: null,
      approvedPvm: null,
      invoicingPvm: "",
      description: "",
      warrantyFollowUp: "",
      warrantyFollowUpComments: "",
      imageAttachment: null,
      coatingResearchReport: [],
      attachedFiles: [],
      existingFiles: [],
      existingQcFiles: [],
      fileIdsToKeep: [],
      qcFileIdsToKeep: [],
      plannedWorkSteps: [],
      userRightsType: "general",
      rolePermissions: [],
      targetContract: "",
      newObjectName: "",
      drillLocation: null,
      modifier: null,
      modified: null,
      checkedToInvoice: false,
      checkedToInvoiceDate: null,
      checkedToInvoiceUserName: null,
      signingLocked: false,
      implementedComment: "",
      measurementDate: null,
      measurementPersons: "",
      contractorWorkNumber: "",
      ...STARA_OBJECT_FIELD_DEFAULTS,
    },
    validate: {
      job: (value: string) =>
        !value || !value.trim()
          ? getTranslation("LABEL_ADD_ACTION_JOB_REQUIRED") || "Job is required"
          : null,
      name: (value: string) =>
        !value.trim()
          ? getTranslation("LABEL_ADD_ACTION_NAME_REQUIRED") ||
            "Name is required"
          : null,
      status: (value: string) =>
        !value || !value.trim()
          ? getTranslation("LABEL_ADD_ACTION_STATUS_REQUIRED") ||
            "Status is required"
          : null,
      type: (value: string) =>
        !value || !value.trim()
          ? getTranslation("LABEL_ADD_ACTION_TYPE_REQUIRED") ||
            "Type is required"
          : null,
      // wkt validation removed - handled separately with notification
    },
  });

  const handleSubmit = async (values: ObjectData) => {
    // Validate form first
    const validation = form.validate();
    if (validation.hasErrors) {
      return;
    }

    // Check if wkt is present before proceeding
    if (!values.wkt || !values.wkt.trim()) {
      notifications.show({
        title:
          getTranslation("LABEL_ADD_ACTION_GEOMETRY_REQUIRED") ||
          "Geometry Required",
        message:
          getTranslation("LABEL_ADD_ACTION_DRAW_GEOMETRY") ||
          "Please draw a geometry on the map",
        color: "red",
        autoClose: 5000,
        position: "bottom-left",
        classNames: {
          root: "notification-error",
        },
      });
      return;
    }

    setIsCreating(true);
    try {
      // Convert files to base64
      const filePromises = values.attachedFiles.map(async (fileItem) => {
        return new Promise<{
          fileName: string;
          fileHeader: string;
          fileData: string;
          contentType: string;
        }>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const base64 = (reader.result as string).split(",")[1]; // Remove data:image/png;base64, prefix
            resolve({
              fileName: fileItem.file.name,
              fileHeader: fileItem.fileHeader,
              fileData: base64,
              contentType: fileItem.file.type,
            });
          };
          reader.onerror = reject;
          reader.readAsDataURL(fileItem.file);
        });
      });

      const files = await Promise.all(filePromises);

      const toLocalDateString = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}T00:00:00`;
      };

      const requestPayload: CreateGeometryRequest = {
        contractId: parseInt(values.job) || 0,
        wkt: values.wkt,
        drawingType: values.drawingType,
        creatorId: currentUserId,
        strengthId: values.type ? parseInt(values.type) : 0,
        startDate: values.agreedPvm ? toLocalDateString(values.agreedPvm) : null,
        location: values.name,
        description: values.description || null,
        typeComment: values.measures,
        addressSystemId: selectedAddressSystem.id,
        streetName: values.streetName,
        readyDate: values.completedPvm
          ? toLocalDateString(values.completedPvm)
          : null,
        orderNumber: values.orderName || null,
        qualityControlId: values.qualityControl
          ? parseInt(values.qualityControl)
          : null,
        extraText1: values.qualityControlComments || "",
        extraText2: values.warrantyFollowUpComments || "",
        plannedDate: values.pvm ? toLocalDateString(values.pvm) : null,
        approvedDate: values.approvedPvm
          ? toLocalDateString(values.approvedPvm)
          : null,
        lanes: values.lanes || "",
        networkNumber: values.networkName || null,
        foremanId: values.foreman?.id || null,
        foremanName: values.foreman?.name || null,
        contractorForemanId: values.contractorForeman?.id || null,
        roleAccessInUse: values.userRightsType === "site-specific" ? 1 : 0,
        guaranteeFollowId: values.warrantyFollowUp
          ? parseInt(values.warrantyFollowUp)
          : null,
        stateId: values.status ? parseInt(values.status) : 0,
        typeId: values.type ? parseInt(values.type) : null,
        goals: values.levelOfReadiness || "",

        // Role permissions (only included if site-specific)
        rolePermissions:
          values.userRightsType === "site-specific"
            ? values.rolePermissions.map((rp) => ({
                roleId: rp.roleId,
                accessLevel: rp.accessLevel,
              }))
            : null,

        // Files with headers
        files: files.length > 0 ? files : null,

        // porvoo
        subscriberId: values.subscriber?.id || null,
        subscriberName: values.subscriber?.name || "",
        projectManagerId: values.projectManager?.id || null,
        projectManagerName: values.projectManager?.name || null,

        qualityControlId2: values.qualityControl2
          ? parseInt(values.qualityControl2)
          : null,
        objectLen: values.objectLength,
        publishToApi: values.publishToApi ? 1 : 0,
        officeUserId: values.officeUser ? parseInt(values.officeUser) : null,
        hasRoadMarkings: values.roadMarkings ? 1 : 0,
        ordererReferenceNumber: values.ordererReferenceNumber || null,
        pavementActionTypeId: values.pavementActionType
          ? parseInt(values.pavementActionType)
          : null,
        pavementGroundTypeId: values.pavementGroundType
          ? parseInt(values.pavementGroundType)
          : null,
        invoiceNumber: values.invoiceNumber || null,
        invoiced: values.invoiced ? 1 : 0,
        allowedToInvoice: values.allowedToInvoice ? 1 : 0,
        startDistance: values.startDistance,
        endDistance: values.endDistance,
        siteNumber: values.plannedYear
          ? String(values.plannedYear.getFullYear())
          : null,
        vehicleRelocationNoticeAdded: values.vehicleRelocationNoticeAdded
          ? toLocalDateString(values.vehicleRelocationNoticeAdded)
          : null,
        vehicleRelocationNoticeStart: values.vehicleRelocationNoticeStart
          ? toLocalDateString(values.vehicleRelocationNoticeStart)
          : null,
        vehicleRelocationNoticeEnd: values.vehicleRelocationNoticeEnd
          ? toLocalDateString(values.vehicleRelocationNoticeEnd)
          : null,
      };

      // Call the appropriate service function based on drawing type
      let response;
      switch (values.drawingType.toLowerCase()) {
        case "line":
          response = await createPolyline(requestPayload);
          break;
        case "point":
          response = await createPolypoint(requestPayload);
          break;
        case "polygon":
          response = await createPolygon(requestPayload);
          break;
        default:
          notifications.show({
            title: getTranslation("LABEL_ADD_ACTION_ERROR_TITLE") || "Error",
            message:
              getTranslation("LABEL_ADD_ACTION_INVALID_DRAWING_TYPE") ||
              "Invalid drawing type. Please draw a valid geometry.",
            color: "red",
            autoClose: 5000,
            position: "bottom-left",
            classNames: {
              root: "notification-error",
            },
          });
          return;
      }

      //console.log("Geometry created successfully:", response);
      setShowSuccessAlert(true);
      handleClear();

      notifications.show({
        title: getTranslation("LABEL_ADD_ACTION_SUCCESS_TITLE") || "Success",
        message:
          getTranslation("LABEL_ADD_ACTION_CREATED_SUCCESS") ||
          "New action created successfully!",
        color: "green",
        autoClose: 5000,
        position: "bottom-left",
        classNames: {
          root: "notification-success",
        },
      });

      // Refresh geometries after addition of new object
      try {
        const state = useTransientStore.getState();
        const { selectedAddressSystem, geometrySearchFilters } = state;

        if (selectedAddressSystem?.id) {
          // Fetch all available contracts for the user
          const contracts = await getContractsByAddressSystemAndUser(
            selectedAddressSystem.id,
            currentUserId,
          );

          // Find the contract that was used to create this geometry
          const createdWithContractId = parseInt(values.job);
          const createdWithContract = (
            contracts as Array<{
              id: number;
              contractStatusDefaultValue?: number;
            }>
          ).find((c) => c.id === createdWithContractId);

          // Determine which status to show based on the contract used
          const isCreatedWithActiveContract =
            createdWithContract?.contractStatusDefaultValue === 1;
          const statusToShow: "active" | "archived" =
            isCreatedWithActiveContract ? "active" : "archived";

          // Get contract ids for the matching status
          const contractIds = (
            contracts as Array<{
              id: number;
              contractStatusDefaultValue?: number;
            }>
          )
            .filter((c) => {
              if (isCreatedWithActiveContract) {
                return c.contractStatusDefaultValue === 1;
              } else {
                return c.contractStatusDefaultValue !== 1;
              }
            })
            .map((c) => c.id);

          // Set filter
          state.setGeometrySearchFilters({
            ...geometrySearchFilters,
            contractStatus: statusToShow,
            selectedContractIds: contractIds,
          });

          // Refetch search results with updated filters
          const searchResponse = await searchGeometries(
            selectedAddressSystem.id,
            {
              ...geometrySearchFilters,
              contractStatus: statusToShow,
              selectedContractIds: contractIds,
            },
          );

          state.updateSearchResults(
            searchResponse.results,
            searchResponse.polylines,
            searchResponse.polygons,
            searchResponse.points,
          );

          // Clear the drawing layer after geometries are refreshed
          triggerClearDrawing();
        }
      } catch (error) {
        console.error("Failed to refresh geometries after creation:", error);
      }

      setTimeout(() => {
        setShowSuccessAlert(false);
      }, 2000);
    } catch (error: any) {
      console.error("Error creating geometry:", error);
      const errorMessage =
        error.message ||
        getTranslation("LABEL_ADD_ACTION_CREATE_FAILED") ||
        "Failed to create action";
      notifications.show({
        title: getTranslation("LABEL_ADD_ACTION_ERROR_TITLE") || "Error",
        message: errorMessage,
        color: "red",
        autoClose: 5000,
        position: "bottom-left",
        classNames: {
          root: "notification-error",
        },
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleClear = () => {
    form.reset();
    setShowSuccessAlert(false);
    onDrawingStateChange?.(false); // Stop drawing
    triggerClearDrawing(); // Clear the drawn geometry from map
  };

  if (!canAddObject) {
    return (
      <div>
        <Title order={6} mb="md" pl={16} c="blue">
          {getTranslation("LABEL_ADD_ACTION_TITLE") || "Add New Action"}
        </Title>
        <div style={{ padding: 16, color: "gray" }}>
          {getTranslation("LABEL_ADD_ACTION_NO_PERMISSION") ||
            "You do not have permission to add new objects. Please contact your administrator."}
        </div>
      </div>
    );
  }

  return (
    <>
      {isCreating && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.5)",
            backdropFilter: "blur(3px)",
            zIndex: 1001,
            pointerEvents: "all",
          }}
        >
          <LoadingOverlay
            visible={true}
            loaderProps={{ size: "lg" }}
            styles={{
              root: {
                position: "relative",
                width: "100%",
                height: "100%",
                backgroundColor: "transparent",
              },
            }}
          />
        </div>
      )}

      <Title order={6} mb="md" pl={16} pt={5} c="blue">
        {getTranslation("LABEL_ADD_ACTION_TITLE") || "Add New Action"}
      </Title>

      <InformationTab
        form={form}
        showSuccessAlert={showSuccessAlert}
        handleSubmit={handleSubmit}
        handleClose={handleClear}
        mode="create"
        addressSystemId={selectedAddressSystem.id}
        userId={currentUserId}
        onDrawingStart={() => onDrawingStateChange?.(true)}
        onDrawingStop={() => onDrawingStateChange?.(false)}
        canModify={canAddObject}
        stickyButtons
      />
    </>
  );
}
